/**
 * Greeting phrases for the dashboard, by language.
 *
 * This is deliberately a hand-written table rather than an i18n bundle: the app is
 * English-only and stays that way. The greeting is a one-line flourish — we say hello in
 * the language of wherever the person is sitting, then carry on in English. Nothing else
 * on the page is translated.
 *
 * Every non-Latin-script phrase carries a `roman` transliteration (shown as a subtitle) and
 * every phrase carries a `meaning` gloss (used for the screen-reader label and the hover
 * title), so the greeting is never an unreadable smudge to someone who doesn't read the
 * script.
 */

export type TimeBucket = 'morning' | 'afternoon' | 'evening' | 'night';

export type GreetingVariant = {
  /** The greeting as it is actually written in the language. */
  text: string;
  /** Latin transliteration — only for languages not written in Latin script. */
  roman?: string;
  /** Plain-English gloss, e.g. "Good morning". Used for a11y and the tooltip. */
  meaning: string;
};

export type LanguageGreetings = {
  /** English name of the language, for the tooltip: "…in Japanese". */
  label: string;
  /** BCP-47 tag for the `lang` attribute on the rendered greeting. */
  tag: string;
  /** Right-to-left script — the greeting span needs `dir="rtl"`. */
  rtl?: boolean;
  morning: GreetingVariant[];
  afternoon: GreetingVariant[];
  evening: GreetingVariant[];
  /** Optional; falls back to `evening` when absent. */
  night?: GreetingVariant[];
  /** Time-agnostic "hi"-style greetings, always in the pool. */
  anytime: GreetingVariant[];
  /**
   * Country-specific extras, keyed by ISO-3166 alpha-2. Added to the pool when the person is
   * in that country — "G'day" in Australia, "Grüezi" in Switzerland.
   */
  regional?: Record<string, GreetingVariant[]>;
};

const GREETINGS: Record<string, LanguageGreetings> = {
  en: {
    label: 'English',
    tag: 'en',
    morning: [
      { text: 'Good morning', meaning: 'Good morning' },
      { text: 'Morning', meaning: 'Good morning' },
    ],
    afternoon: [
      { text: 'Good afternoon', meaning: 'Good afternoon' },
      { text: 'Afternoon', meaning: 'Good afternoon' },
    ],
    evening: [
      { text: 'Good evening', meaning: 'Good evening' },
      { text: 'Evening', meaning: 'Good evening' },
    ],
    anytime: [
      { text: 'Hi', meaning: 'Hi' },
      { text: 'Hey', meaning: 'Hey' },
      { text: 'Hello', meaning: 'Hello' },
    ],
    regional: {
      AU: [{ text: "G'day", meaning: 'Hello' }],
      NZ: [
        { text: "G'day", meaning: 'Hello' },
        { text: 'Kia ora', meaning: 'Hello (Māori)' },
      ],
      IE: [{ text: 'Dia duit', meaning: 'Hello (Irish)' }],
      ZA: [{ text: 'Howzit', meaning: 'Hello' }],
      GB: [{ text: 'Alright', meaning: 'Hello' }],
    },
  },

  de: {
    label: 'German',
    tag: 'de',
    morning: [
      { text: 'Guten Morgen', meaning: 'Good morning' },
      { text: 'Morgen', meaning: 'Morning' },
    ],
    afternoon: [{ text: 'Guten Tag', meaning: 'Good day' }],
    evening: [
      { text: 'Guten Abend', meaning: 'Good evening' },
      { text: "'n Abend", meaning: 'Evening' },
    ],
    // No bare "Hi" here even though Germans say it — it is indistinguishable from the
    // English greeting, so it reads as the feature having failed.
    anytime: [
      { text: 'Hallo', meaning: 'Hello' },
      { text: 'Na', meaning: 'Hey' },
    ],
    regional: {
      AT: [
        { text: 'Servus', meaning: 'Hello' },
        { text: 'Grüß Gott', meaning: 'Hello' },
      ],
      CH: [
        { text: 'Grüezi', meaning: 'Hello' },
        { text: 'Hoi', meaning: 'Hi' },
      ],
    },
  },

  nl: {
    label: 'Dutch',
    tag: 'nl',
    morning: [{ text: 'Goedemorgen', meaning: 'Good morning' }],
    afternoon: [{ text: 'Goedemiddag', meaning: 'Good afternoon' }],
    evening: [{ text: 'Goedenavond', meaning: 'Good evening' }],
    anytime: [
      { text: 'Hoi', meaning: 'Hi' },
      { text: 'Hallo', meaning: 'Hello' },
      { text: 'Hé', meaning: 'Hey' },
    ],
  },

  fr: {
    label: 'French',
    tag: 'fr',
    morning: [{ text: 'Bonjour', meaning: 'Good morning' }],
    afternoon: [
      { text: 'Bonjour', meaning: 'Good day' },
      { text: 'Bon après-midi', meaning: 'Good afternoon' },
    ],
    evening: [{ text: 'Bonsoir', meaning: 'Good evening' }],
    anytime: [
      { text: 'Salut', meaning: 'Hi' },
      { text: 'Coucou', meaning: 'Hey there' },
    ],
    regional: {
      CA: [{ text: 'Allô', meaning: 'Hi' }],
    },
  },

  es: {
    label: 'Spanish',
    tag: 'es',
    morning: [{ text: 'Buenos días', meaning: 'Good morning' }],
    afternoon: [{ text: 'Buenas tardes', meaning: 'Good afternoon' }],
    evening: [{ text: 'Buenas tardes', meaning: 'Good evening' }],
    night: [{ text: 'Buenas noches', meaning: 'Good night' }],
    anytime: [
      { text: 'Hola', meaning: 'Hello' },
      { text: '¿Qué tal?', meaning: "How's it going?" },
    ],
    regional: {
      MX: [{ text: '¿Qué onda?', meaning: "What's up?" }],
      AR: [{ text: '¿Todo bien?', meaning: 'All good?' }],
    },
  },

  ca: {
    label: 'Catalan',
    tag: 'ca',
    morning: [{ text: 'Bon dia', meaning: 'Good morning' }],
    afternoon: [{ text: 'Bona tarda', meaning: 'Good afternoon' }],
    evening: [{ text: 'Bona tarda', meaning: 'Good evening' }],
    night: [{ text: 'Bona nit', meaning: 'Good night' }],
    anytime: [{ text: 'Hola', meaning: 'Hello' }],
  },

  it: {
    label: 'Italian',
    tag: 'it',
    morning: [{ text: 'Buongiorno', meaning: 'Good morning' }],
    afternoon: [{ text: 'Buon pomeriggio', meaning: 'Good afternoon' }],
    evening: [{ text: 'Buonasera', meaning: 'Good evening' }],
    anytime: [
      { text: 'Ciao', meaning: 'Hi' },
      { text: 'Salve', meaning: 'Hello' },
    ],
  },

  pt: {
    label: 'Portuguese',
    tag: 'pt',
    morning: [{ text: 'Bom dia', meaning: 'Good morning' }],
    afternoon: [{ text: 'Boa tarde', meaning: 'Good afternoon' }],
    evening: [{ text: 'Boa noite', meaning: 'Good evening' }],
    anytime: [
      { text: 'Olá', meaning: 'Hello' },
      { text: 'Oi', meaning: 'Hi' },
    ],
    regional: {
      BR: [
        { text: 'E aí', meaning: "What's up?" },
        { text: 'Fala', meaning: 'Hey' },
      ],
    },
  },

  sv: {
    label: 'Swedish',
    tag: 'sv',
    morning: [{ text: 'God morgon', meaning: 'Good morning' }],
    afternoon: [{ text: 'God eftermiddag', meaning: 'Good afternoon' }],
    evening: [{ text: 'God kväll', meaning: 'Good evening' }],
    anytime: [
      { text: 'Hej', meaning: 'Hi' },
      { text: 'Tjena', meaning: 'Hey' },
      { text: 'Hejsan', meaning: 'Hello there' },
    ],
  },

  nb: {
    label: 'Norwegian',
    tag: 'nb',
    morning: [{ text: 'God morgen', meaning: 'Good morning' }],
    afternoon: [{ text: 'God ettermiddag', meaning: 'Good afternoon' }],
    evening: [{ text: 'God kveld', meaning: 'Good evening' }],
    anytime: [
      { text: 'Hei', meaning: 'Hi' },
      { text: 'Hallo', meaning: 'Hello' },
    ],
  },

  da: {
    label: 'Danish',
    tag: 'da',
    morning: [{ text: 'Godmorgen', meaning: 'Good morning' }],
    afternoon: [{ text: 'God eftermiddag', meaning: 'Good afternoon' }],
    evening: [{ text: 'Godaften', meaning: 'Good evening' }],
    anytime: [
      { text: 'Hej', meaning: 'Hi' },
      { text: 'Hejsa', meaning: 'Hello there' },
    ],
  },

  fi: {
    label: 'Finnish',
    tag: 'fi',
    morning: [{ text: 'Huomenta', meaning: 'Good morning' }],
    afternoon: [{ text: 'Hyvää iltapäivää', meaning: 'Good afternoon' }],
    evening: [{ text: 'Hyvää iltaa', meaning: 'Good evening' }],
    anytime: [
      { text: 'Moi', meaning: 'Hi' },
      { text: 'Terve', meaning: 'Hello' },
      { text: 'Hei', meaning: 'Hi' },
    ],
  },

  is: {
    label: 'Icelandic',
    tag: 'is',
    morning: [{ text: 'Góðan daginn', meaning: 'Good day' }],
    afternoon: [{ text: 'Góðan daginn', meaning: 'Good day' }],
    evening: [{ text: 'Gott kvöld', meaning: 'Good evening' }],
    anytime: [
      { text: 'Halló', meaning: 'Hello' },
      { text: 'Hæ', meaning: 'Hi' },
    ],
  },

  pl: {
    label: 'Polish',
    tag: 'pl',
    morning: [{ text: 'Dzień dobry', meaning: 'Good day' }],
    afternoon: [{ text: 'Dzień dobry', meaning: 'Good day' }],
    evening: [{ text: 'Dobry wieczór', meaning: 'Good evening' }],
    anytime: [
      { text: 'Cześć', meaning: 'Hi' },
      { text: 'Hej', meaning: 'Hey' },
    ],
  },

  cs: {
    label: 'Czech',
    tag: 'cs',
    morning: [{ text: 'Dobré ráno', meaning: 'Good morning' }],
    afternoon: [{ text: 'Dobrý den', meaning: 'Good day' }],
    evening: [{ text: 'Dobrý večer', meaning: 'Good evening' }],
    anytime: [
      { text: 'Ahoj', meaning: 'Hi' },
      { text: 'Čau', meaning: 'Hey' },
    ],
  },

  sk: {
    label: 'Slovak',
    tag: 'sk',
    morning: [{ text: 'Dobré ráno', meaning: 'Good morning' }],
    afternoon: [{ text: 'Dobrý deň', meaning: 'Good day' }],
    evening: [{ text: 'Dobrý večer', meaning: 'Good evening' }],
    anytime: [
      { text: 'Ahoj', meaning: 'Hi' },
      { text: 'Čau', meaning: 'Hey' },
    ],
  },

  hu: {
    label: 'Hungarian',
    tag: 'hu',
    morning: [{ text: 'Jó reggelt', meaning: 'Good morning' }],
    afternoon: [{ text: 'Jó napot', meaning: 'Good day' }],
    evening: [{ text: 'Jó estét', meaning: 'Good evening' }],
    anytime: [
      { text: 'Szia', meaning: 'Hi' },
      { text: 'Helló', meaning: 'Hello' },
    ],
  },

  ro: {
    label: 'Romanian',
    tag: 'ro',
    morning: [{ text: 'Bună dimineața', meaning: 'Good morning' }],
    afternoon: [{ text: 'Bună ziua', meaning: 'Good day' }],
    evening: [{ text: 'Bună seara', meaning: 'Good evening' }],
    anytime: [
      { text: 'Bună', meaning: 'Hi' },
      { text: 'Salut', meaning: 'Hey' },
    ],
  },

  hr: {
    label: 'Croatian',
    tag: 'hr',
    morning: [{ text: 'Dobro jutro', meaning: 'Good morning' }],
    afternoon: [{ text: 'Dobar dan', meaning: 'Good day' }],
    evening: [{ text: 'Dobra večer', meaning: 'Good evening' }],
    anytime: [
      { text: 'Bok', meaning: 'Hi' },
      { text: 'Zdravo', meaning: 'Hello' },
    ],
  },

  sl: {
    label: 'Slovenian',
    tag: 'sl',
    morning: [{ text: 'Dobro jutro', meaning: 'Good morning' }],
    afternoon: [{ text: 'Dober dan', meaning: 'Good day' }],
    evening: [{ text: 'Dober večer', meaning: 'Good evening' }],
    anytime: [
      { text: 'Živjo', meaning: 'Hi' },
      { text: 'Zdravo', meaning: 'Hello' },
    ],
  },

  et: {
    label: 'Estonian',
    tag: 'et',
    morning: [{ text: 'Tere hommikust', meaning: 'Good morning' }],
    afternoon: [{ text: 'Tere päevast', meaning: 'Good day' }],
    evening: [{ text: 'Tere õhtust', meaning: 'Good evening' }],
    anytime: [{ text: 'Tere', meaning: 'Hello' }],
  },

  lv: {
    label: 'Latvian',
    tag: 'lv',
    morning: [{ text: 'Labrīt', meaning: 'Good morning' }],
    afternoon: [{ text: 'Labdien', meaning: 'Good day' }],
    evening: [{ text: 'Labvakar', meaning: 'Good evening' }],
    anytime: [{ text: 'Sveiki', meaning: 'Hello' }],
  },

  lt: {
    label: 'Lithuanian',
    tag: 'lt',
    morning: [{ text: 'Labas rytas', meaning: 'Good morning' }],
    afternoon: [{ text: 'Laba diena', meaning: 'Good day' }],
    evening: [{ text: 'Labas vakaras', meaning: 'Good evening' }],
    anytime: [
      { text: 'Labas', meaning: 'Hi' },
      { text: 'Sveiki', meaning: 'Hello' },
    ],
  },

  el: {
    label: 'Greek',
    tag: 'el',
    morning: [{ text: 'Καλημέρα', roman: 'kaliméra', meaning: 'Good morning' }],
    afternoon: [{ text: 'Καλό απόγευμα', roman: 'kaló apógevma', meaning: 'Good afternoon' }],
    evening: [{ text: 'Καλησπέρα', roman: 'kalispéra', meaning: 'Good evening' }],
    anytime: [
      { text: 'Γεια σου', roman: 'yiá sou', meaning: 'Hello' },
      { text: 'Γεια', roman: 'yiá', meaning: 'Hi' },
    ],
  },

  ru: {
    label: 'Russian',
    tag: 'ru',
    morning: [{ text: 'Доброе утро', roman: 'dobroye utro', meaning: 'Good morning' }],
    afternoon: [{ text: 'Добрый день', roman: 'dobry den', meaning: 'Good day' }],
    evening: [{ text: 'Добрый вечер', roman: 'dobry vecher', meaning: 'Good evening' }],
    anytime: [
      { text: 'Привет', roman: 'privet', meaning: 'Hi' },
      { text: 'Здравствуйте', roman: 'zdravstvuyte', meaning: 'Hello' },
    ],
  },

  uk: {
    label: 'Ukrainian',
    tag: 'uk',
    morning: [{ text: 'Доброго ранку', roman: 'dobroho ranku', meaning: 'Good morning' }],
    afternoon: [{ text: 'Добрий день', roman: 'dobry den', meaning: 'Good day' }],
    evening: [{ text: 'Добрий вечір', roman: 'dobry vechir', meaning: 'Good evening' }],
    anytime: [
      { text: 'Привіт', roman: 'pryvit', meaning: 'Hi' },
      { text: 'Вітаю', roman: 'vitayu', meaning: 'Greetings' },
    ],
  },

  bg: {
    label: 'Bulgarian',
    tag: 'bg',
    morning: [{ text: 'Добро утро', roman: 'dobro utro', meaning: 'Good morning' }],
    afternoon: [{ text: 'Добър ден', roman: 'dobar den', meaning: 'Good day' }],
    evening: [{ text: 'Добър вечер', roman: 'dobar vecher', meaning: 'Good evening' }],
    anytime: [
      { text: 'Здравей', roman: 'zdravey', meaning: 'Hello' },
      { text: 'Здрасти', roman: 'zdrasti', meaning: 'Hi' },
    ],
  },

  sr: {
    label: 'Serbian',
    tag: 'sr',
    morning: [{ text: 'Добро јутро', roman: 'dobro jutro', meaning: 'Good morning' }],
    afternoon: [{ text: 'Добар дан', roman: 'dobar dan', meaning: 'Good day' }],
    evening: [{ text: 'Добро вече', roman: 'dobro veče', meaning: 'Good evening' }],
    anytime: [
      { text: 'Здраво', roman: 'zdravo', meaning: 'Hello' },
      { text: 'Ћао', roman: 'ćao', meaning: 'Hi' },
    ],
  },

  tr: {
    label: 'Turkish',
    tag: 'tr',
    morning: [{ text: 'Günaydın', meaning: 'Good morning' }],
    afternoon: [{ text: 'İyi günler', meaning: 'Good day' }],
    evening: [{ text: 'İyi akşamlar', meaning: 'Good evening' }],
    anytime: [
      { text: 'Merhaba', meaning: 'Hello' },
      { text: 'Selam', meaning: 'Hi' },
    ],
  },

  he: {
    label: 'Hebrew',
    tag: 'he',
    rtl: true,
    morning: [{ text: 'בוקר טוב', roman: 'boker tov', meaning: 'Good morning' }],
    afternoon: [{ text: 'צהריים טובים', roman: 'tzohorayim tovim', meaning: 'Good afternoon' }],
    evening: [{ text: 'ערב טוב', roman: 'erev tov', meaning: 'Good evening' }],
    anytime: [
      { text: 'שלום', roman: 'shalom', meaning: 'Hello' },
      { text: 'היי', roman: 'hey', meaning: 'Hi' },
    ],
  },

  ar: {
    label: 'Arabic',
    tag: 'ar',
    rtl: true,
    morning: [{ text: 'صباح الخير', roman: 'sabah al-khayr', meaning: 'Good morning' }],
    afternoon: [{ text: 'مساء الخير', roman: 'masaa al-khayr', meaning: 'Good afternoon' }],
    evening: [{ text: 'مساء الخير', roman: 'masaa al-khayr', meaning: 'Good evening' }],
    anytime: [
      { text: 'مرحبا', roman: 'marhaban', meaning: 'Hello' },
      { text: 'أهلاً', roman: 'ahlan', meaning: 'Hi' },
    ],
  },

  hi: {
    label: 'Hindi',
    tag: 'hi',
    morning: [{ text: 'सुप्रभात', roman: 'suprabhāt', meaning: 'Good morning' }],
    afternoon: [{ text: 'नमस्कार', roman: 'namaskār', meaning: 'Greetings' }],
    evening: [{ text: 'शुभ संध्या', roman: 'shubh sandhyā', meaning: 'Good evening' }],
    anytime: [
      { text: 'नमस्ते', roman: 'namaste', meaning: 'Hello' },
      { text: 'नमस्कार', roman: 'namaskār', meaning: 'Greetings' },
    ],
  },

  th: {
    label: 'Thai',
    tag: 'th',
    morning: [{ text: 'อรุณสวัสดิ์', roman: 'arun sawat', meaning: 'Good morning' }],
    afternoon: [{ text: 'สวัสดีตอนบ่าย', roman: 'sawatdee ton bai', meaning: 'Good afternoon' }],
    evening: [{ text: 'สวัสดีตอนเย็น', roman: 'sawatdee ton yen', meaning: 'Good evening' }],
    anytime: [{ text: 'สวัสดี', roman: 'sawatdee', meaning: 'Hello' }],
  },

  vi: {
    label: 'Vietnamese',
    tag: 'vi',
    morning: [{ text: 'Chào buổi sáng', meaning: 'Good morning' }],
    afternoon: [{ text: 'Chào buổi chiều', meaning: 'Good afternoon' }],
    evening: [{ text: 'Chào buổi tối', meaning: 'Good evening' }],
    anytime: [
      { text: 'Xin chào', meaning: 'Hello' },
      { text: 'Chào bạn', meaning: 'Hi there' },
    ],
  },

  id: {
    label: 'Indonesian',
    tag: 'id',
    morning: [{ text: 'Selamat pagi', meaning: 'Good morning' }],
    afternoon: [{ text: 'Selamat siang', meaning: 'Good afternoon' }],
    evening: [{ text: 'Selamat sore', meaning: 'Good evening' }],
    night: [{ text: 'Selamat malam', meaning: 'Good night' }],
    anytime: [
      { text: 'Halo', meaning: 'Hello' },
      { text: 'Hai', meaning: 'Hi' },
    ],
  },

  ms: {
    label: 'Malay',
    tag: 'ms',
    morning: [{ text: 'Selamat pagi', meaning: 'Good morning' }],
    afternoon: [{ text: 'Selamat tengah hari', meaning: 'Good afternoon' }],
    evening: [{ text: 'Selamat petang', meaning: 'Good evening' }],
    night: [{ text: 'Selamat malam', meaning: 'Good night' }],
    anytime: [
      { text: 'Helo', meaning: 'Hello' },
      { text: 'Hai', meaning: 'Hi' },
    ],
  },

  fil: {
    label: 'Filipino',
    tag: 'fil',
    morning: [{ text: 'Magandang umaga', meaning: 'Good morning' }],
    afternoon: [{ text: 'Magandang hapon', meaning: 'Good afternoon' }],
    evening: [{ text: 'Magandang gabi', meaning: 'Good evening' }],
    anytime: [
      { text: 'Kumusta', meaning: 'Hello' },
      { text: 'Kamusta', meaning: 'Hi' },
    ],
  },

  af: {
    label: 'Afrikaans',
    tag: 'af',
    morning: [{ text: 'Goeiemôre', meaning: 'Good morning' }],
    afternoon: [{ text: 'Goeiemiddag', meaning: 'Good afternoon' }],
    evening: [{ text: 'Goeienaand', meaning: 'Good evening' }],
    anytime: [
      { text: 'Haai', meaning: 'Hi' },
      { text: 'Hallo', meaning: 'Hello' },
    ],
  },

  ja: {
    label: 'Japanese',
    tag: 'ja',
    morning: [
      { text: 'おはよう', roman: 'ohayō', meaning: 'Good morning' },
      { text: 'おはようございます', roman: 'ohayō gozaimasu', meaning: 'Good morning' },
    ],
    afternoon: [{ text: 'こんにちは', roman: 'konnichiwa', meaning: 'Good afternoon' }],
    evening: [{ text: 'こんばんは', roman: 'konbanwa', meaning: 'Good evening' }],
    anytime: [
      { text: 'やあ', roman: 'yā', meaning: 'Hi' },
      { text: 'どうも', roman: 'dōmo', meaning: 'Hello' },
    ],
  },

  ko: {
    label: 'Korean',
    tag: 'ko',
    morning: [{ text: '좋은 아침이에요', roman: 'joeun achimieyo', meaning: 'Good morning' }],
    afternoon: [{ text: '안녕하세요', roman: 'annyeonghaseyo', meaning: 'Hello' }],
    evening: [{ text: '좋은 저녁이에요', roman: 'joeun jeonyeogieyo', meaning: 'Good evening' }],
    anytime: [
      { text: '안녕하세요', roman: 'annyeonghaseyo', meaning: 'Hello' },
      { text: '안녕', roman: 'annyeong', meaning: 'Hi' },
    ],
  },

  'zh-Hans': {
    label: 'Chinese',
    tag: 'zh-Hans',
    morning: [{ text: '早上好', roman: 'zǎoshang hǎo', meaning: 'Good morning' }],
    afternoon: [{ text: '下午好', roman: 'xiàwǔ hǎo', meaning: 'Good afternoon' }],
    evening: [{ text: '晚上好', roman: 'wǎnshàng hǎo', meaning: 'Good evening' }],
    anytime: [
      { text: '你好', roman: 'nǐ hǎo', meaning: 'Hello' },
      { text: '嗨', roman: 'hāi', meaning: 'Hi' },
    ],
  },

  'zh-Hant': {
    label: 'Chinese',
    tag: 'zh-Hant',
    morning: [{ text: '早安', roman: 'zǎo ān', meaning: 'Good morning' }],
    afternoon: [{ text: '午安', roman: 'wǔ ān', meaning: 'Good afternoon' }],
    evening: [{ text: '晚安', roman: 'wǎn ān', meaning: 'Good evening' }],
    anytime: [
      { text: '你好', roman: 'nǐ hǎo', meaning: 'Hello' },
      { text: '哈囉', roman: 'hā luō', meaning: 'Hi' },
    ],
  },
};

export default GREETINGS;
